<template>
  <teleport to="body">
    <div v-if="visible" class="notif-modal-backdrop" @click.self="$emit('close')">
      <div class="notif-modal" role="dialog" aria-modal="true">

        <!-- Modal header -->
        <div class="notif-modal-header">
          <div class="notif-modal-header-left">
            <span class="notif-modal-icon" :class="`notif-icon-${notifTypeClass}`">
              <i :class="notifIcon"></i>
            </span>
            <div>
              <div class="notif-modal-title">{{ modalTitle }}</div>
              <div class="notif-modal-subtitle" v-if="notification">{{ notificationBody(notification) }}</div>
            </div>
          </div>
          <button type="button" class="notif-modal-close" @click="$emit('close')" aria-label="Cerrar">
            <i class="fa fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="notif-modal-body">

          <!-- Loading -->
          <div v-if="loading" class="notif-modal-loading">
            <i class="fa fa-spinner fa-spin"></i> Cargando...
          </div>

          <!-- Error -->
          <div v-else-if="error" class="notif-modal-error">{{ error }}</div>

          <!-- Empty -->
          <div v-else-if="cfdis.length === 0" class="notif-modal-empty">
            No se encontraron CFDIs asociados a esta notificación.
          </div>

          <!-- ────────────────────────────────────────────────────
               DOWNLOAD NOTIFICATION → table list
          ──────────────────────────────────────────────────── -->
          <template v-else-if="isDownload">
            <div class="table-responsive">
              <table class="table table-sm notif-cfdi-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>UUID</th>
                    <th>Tipo</th>
                    <th>RFC Emisor</th>
                    <th>Nombre Emisor</th>
                    <th>RFC Receptor</th>
                    <th>Nombre Receptor</th>
                    <th>Fecha</th>
                    <th class="text-end">Total</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="cfdi in cfdis" :key="cfdi.id">
                    <td>
                      <button
                        type="button"
                        class="btn btn-outline-primary btn-xxs"
                        :disabled="!cfdi.uuid"
                        @click="downloadXml(cfdi)"
                        title="Descargar XML"
                      >XML</button>
                    </td>
                    <td class="cfdi-uuid-cell">{{ cfdi.uuid }}</td>
                    <td>{{ comprobanteTipoLabel(cfdi.tipo_de_comprobante) }}</td>
                    <td>{{ cfdi.rfc_emisor }}</td>
                    <td class="cfdi-name-cell">{{ cfdi.nombre_emisor }}</td>
                    <td>{{ cfdi.rfc_receptor }}</td>
                    <td class="cfdi-name-cell">{{ cfdi.nombre_receptor }}</td>
                    <td>{{ cfdi.fecha }}</td>
                    <td class="text-end">{{ fmtCurrency(cfdi.total) }}</td>
                    <td>
                      <span class="cfdi-estado-badge" :class="`estado-${estadoClass(cfdi.estado)}`">
                        {{ cfdi.estado || '–' }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <!-- ────────────────────────────────────────────────────
               CANCELLATION NOTIFICATION
               - If 1 CFDI: show preview directly
               - If many: show list → click → preview
          ──────────────────────────────────────────────────── -->
          <template v-else-if="isCancellation">

            <!-- Preview view -->
            <template v-if="view === 'preview'">
              <div class="notif-preview-nav" v-if="cfdis.length > 1">
                <button type="button" class="btn-back" @click="backToList">
                  <i class="fa fa-arrow-left"></i> Volver a la lista
                </button>
              </div>

              <div v-if="previewLoading" class="notif-modal-loading">
                <i class="fa fa-spinner fa-spin"></i> Cargando detalle...
              </div>
              <div v-else-if="previewError" class="notif-modal-error">{{ previewError }}</div>
              <CfdiPreview
                v-else-if="previewData"
                :cfdi="previewData.cfdi"
                :emisor="previewData.emisor"
                :receptor="previewData.receptor"
                :conceptos="previewData.conceptos"
                :totales="previewData.totales"
              />
            </template>

            <!-- List view (multiple UUIDs) -->
            <template v-else>
              <div class="table-responsive">
                <table class="table table-sm notif-cfdi-table">
                  <thead>
                    <tr>
                      <th>UUID</th>
                      <th>Tipo</th>
                      <th>RFC Emisor</th>
                      <th>RFC Receptor</th>
                      <th>Fecha</th>
                      <th class="text-end">Total</th>
                      <th>Estado</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="cfdi in cfdis" :key="cfdi.id">
                      <td class="cfdi-uuid-cell">{{ cfdi.uuid }}</td>
                      <td>{{ comprobanteTipoLabel(cfdi.tipo_de_comprobante) }}</td>
                      <td>{{ cfdi.rfc_emisor }}</td>
                      <td>{{ cfdi.rfc_receptor }}</td>
                      <td>{{ cfdi.fecha }}</td>
                      <td class="text-end">{{ fmtCurrency(cfdi.total) }}</td>
                      <td>
                        <span class="cfdi-estado-badge" :class="`estado-${estadoClass(cfdi.estado)}`">
                          {{ cfdi.estado || '–' }}
                        </span>
                      </td>
                      <td>
                        <button type="button" class="btn-preview" @click="openPreview(cfdi)" title="Ver detalle">
                          <i class="fa fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>

          </template>

        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import CfdiPreview from '@/components/cfdis/CfdiPreview.vue';
import notificationsService from '@/services/notifications';
import cfdisService from '@/services/cfdis';

const TIPO_MAP = {
  I: 'Factura',
  E: 'Nota de Crédito',
  P: 'Complemento de Pago',
  N: 'Nómina',
  T: 'Traslado',
};

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default {
  name: 'NotificationDetailModal',
  components: { CfdiPreview },
  emits: ['close'],
  props: {
    visible:      { type: Boolean, default: false },
    notification: { type: Object,  default: null  },
    rfc:          { type: String,  default: ''    },
  },
  data() {
    return {
      loading:       false,
      error:         '',
      cfdis:         [],
      view:          'list',
      previewLoading: false,
      previewError:   '',
      previewData:    null,
    };
  },
  computed: {
    isDownload() {
      const t = this.notification?.type || '';
      return t === 'download_received' || t === 'download_issued';
    },
    isCancellation() {
      const t = this.notification?.type || '';
      return t === 'cfdi_cancelled' || t === 'cfdi_cancellation_in_progress';
    },
    notifTypeClass() {
      if (this.isDownload)     return 'download';
      if (this.isCancellation) return 'cancel';
      return 'default';
    },
    notifIcon() {
      if (this.isDownload)     return 'fa fa-download';
      if (this.isCancellation) return 'fa fa-ban';
      return 'fa fa-bell';
    },
    modalTitle() {
      return this.notification?.title || 'Detalle de notificación';
    },
  },
  watch: {
    visible(val) {
      if (val && this.notification) this.fetchCfdis();
    },
    notification(val) {
      if (val && this.visible) this.fetchCfdis();
    },
  },
  methods: {
    extractPayload(response) {
      const isRaw = !!(response && typeof response === 'object' && 'status' in response && 'config' in response);
      return isRaw ? response.data : response;
    },
    async fetchCfdis() {
      this.loading = true;
      this.error   = '';
      this.cfdis   = [];
      this.view    = 'list';
      this.previewData = null;

      try {
        const response = await notificationsService.getNotificationCfdis(this.notification.id);
        const payload  = this.extractPayload(response);
        const rows     = Array.isArray(payload?.data) ? payload.data : [];
        this.cfdis     = rows;

        // If single-UUID cancellation, jump straight to preview
        if (this.isCancellation && this.cfdis.length === 1) {
          this.openPreview(this.cfdis[0]);
        }
      } catch (_) {
        this.error = 'No fue posible cargar los CFDIs de esta notificación.';
      }

      this.loading = false;
    },
    async openPreview(cfdi) {
      this.view         = 'preview';
      this.previewLoading = true;
      this.previewError   = '';
      this.previewData    = null;

      const rfc = this.rfc || localStorage.getItem('rfc') || '';

      try {
        const response = await cfdisService.getCfdiDetail(rfc, cfdi.uuid);
        const payload  = this.extractPayload(response);

        if (payload?.type === 'success' && payload?.data) {
          this.previewData = payload.data;
        } else {
          this.previewError = payload?.message || 'No se pudo cargar el detalle del CFDI.';
        }
      } catch (_) {
        this.previewError = 'No fue posible obtener el detalle del CFDI.';
      }

      this.previewLoading = false;
    },
    backToList() {
      this.view        = 'list';
      this.previewData = null;
    },
    notificationBody(n) {
      const body      = String(n?.body || '');
      const monthsMap = { '01':'Enero','02':'Febrero','03':'Marzo','04':'Abril','05':'Mayo','06':'Junio','07':'Julio','08':'Agosto','09':'Septiembre','10':'Octubre','11':'Noviembre','12':'Diciembre' };
      return body.replace(/\b(\d{4})-(0[1-9]|1[0-2])\b/g, (_, year, month) => {
        const name = monthsMap[month];
        return name ? `${name} ${year}` : `${year}-${month}`;
      });
    },
    comprobanteTipoLabel(tipo) {
      return TIPO_MAP[String(tipo || '').toUpperCase()] || String(tipo || '–');
    },
    estadoClass(estado) {
      const s = String(estado || '').toUpperCase();
      if (s === 'VIGENTE')   return 'vigente';
      if (s === 'CANCELADO') return 'cancelado';
      return 'unknown';
    },
    fmtCurrency(value) {
      const n = Number(String(value ?? 0).replace(/,/g, ''));
      const formatted = new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN',
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(isNaN(n) ? 0 : n);
      return formatted.replace(/^\$/, '$ ').replace(/^-\$/, '-$ ');
    },
    async downloadXml(cfdi) {
      const uuid = String(cfdi.uuid || '').toUpperCase();
      const rfc  = this.rfc || localStorage.getItem('rfc') || '';
      if (!uuid || !rfc) return;

      try {
        const dashboardWs = (await import('@/services/dashboard')).default;
        const response    = await dashboardWs.getDashboardCfdiXml(rfc, uuid, { skipLoading: true });
        const blob        = response instanceof Blob ? response : new Blob([response], { type: 'application/xml;charset=utf-8' });
        const blobUrl     = window.URL.createObjectURL(blob);
        const link        = document.createElement('a');
        link.href         = blobUrl;
        link.download     = `${uuid}.xml`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (_) {
        if (this.$toast?.error) this.$toast.error('No fue posible descargar el XML seleccionado.');
      }
    },
  },
};
</script>

<style scoped>
/* ── Backdrop ── */
.notif-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1055;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: backdropIn 0.15s ease-out;
}

@keyframes backdropIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* ── Modal box ── */
.notif-modal {
  background: var(--white);
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,.22);
  width: 100%;
  max-width: 900px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: modalSlideIn 0.15s ease-out;
}

@keyframes modalSlideIn {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header ── */
.notif-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--sidebar-border);
  flex-shrink: 0;
}

.notif-modal-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notif-modal-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
}

.notif-icon-download { background: rgba(115,102,255,.12); color: var(--theme-deafult); }
.notif-icon-cancel   { background: rgba(180,35,24,.10);   color: #b42318; }
.notif-icon-default  { background: rgba(100,116,139,.10); color: #64748b; }

.notif-modal-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--theme-font-color);
  line-height: 1.2;
}

.notif-modal-subtitle {
  font-size: 11px;
  color: var(--theme-font-color);
  opacity: .65;
  margin-top: 2px;
}

.notif-modal-close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid var(--sidebar-border);
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--theme-font-color);
  opacity: .6;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.notif-modal-close:hover { opacity: 1; }

/* ── Body ── */
.notif-modal-body {
  overflow-y: auto;
  padding: 16px 18px;
  flex: 1;
}

.notif-modal-loading,
.notif-modal-empty {
  text-align: center;
  padding: 32px 16px;
  font-size: 13px;
  color: var(--theme-font-color);
  opacity: .6;
}

.notif-modal-error {
  padding: 12px 16px;
  background: rgba(180,35,24,.08);
  border-radius: 8px;
  font-size: 12px;
  color: #b42318;
}

/* ── CFDI table ── */
.notif-cfdi-table th,
.notif-cfdi-table td {
  font-size: 11px;
  white-space: nowrap;
  vertical-align: middle;
}

.cfdi-uuid-cell {
  font-family: monospace;
  font-size: 10px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cfdi-name-cell {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cfdi-estado-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
}

.estado-vigente   { background: rgba(15,118,110,.12);  color: #0f766e; }
.estado-cancelado { background: rgba(180,35,24,.10);   color: #b42318; }
.estado-unknown   { background: rgba(100,116,139,.10); color: #64748b; }

.btn-xxs {
  padding: 1px 5px;
  font-size: 10px;
}

/* ── Preview nav ── */
.notif-preview-nav {
  margin-bottom: 12px;
}

.btn-back {
  background: transparent;
  border: 1px solid var(--sidebar-border);
  border-radius: 8px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
  color: var(--theme-default);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.btn-back:hover {
  background: var(--sidebar-border);
}

/* ── Eye button ── */
.btn-preview {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid var(--sidebar-border);
  background: transparent;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: var(--theme-deafult);
  transition: background 0.15s;
}

.btn-preview:hover {
  background: rgba(115,102,255,.08);
}
</style>
